import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {
  filterBuildingsBySearch,
  formatBuildingOptionLabel,
  formatBuildingOptionMeta
} from '@/utils/buildings';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import {
  closeActiveBuildingSelect,
  selectActiveBuildingId,
  selectShowActiveBuildingSelect,
  setActiveBuildingId,
} from '@/features/building/buildingSlice';
import { useGetBuildingsQuery } from "@/api";

const BuildingSelectModal = () => {
  const {
    data: allBuildings
  } = useGetBuildingsQuery();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [chosenBuildinId, setChosenBuildinId] = useState<number | undefined>();
  const open = useSelector(selectShowActiveBuildingSelect);
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const selectedBuildingId = chosenBuildinId ?? activeBuildingId
  const selectBuilding = () => {
    if (!selectedBuildingId) return
    dispatch(setActiveBuildingId(selectedBuildingId));
    dispatch(closeActiveBuildingSelect())
  }
  const activeBuildings =
    allBuildings?.filter((b) => b.is_active !== false) || []
  return (
    <Dialog 
      open={ open } 
      onClose={ () => dispatch(closeActiveBuildingSelect()) }
    >
      <DialogTitle>{t('buildings_list')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('choose_building_text')}</DialogContentText>
        <Box component="form" sx={{ mt: 2 }}>
          <Autocomplete
            options={activeBuildings}
            getOptionLabel={formatBuildingOptionLabel}
            filterOptions={(options, state) =>
              filterBuildingsBySearch(options, state.inputValue)
            }
            value={
              allBuildings?.find((b) => b.id === chosenBuildinId) ||
              allBuildings?.find((b) => b.id === activeBuildingId) ||
              null
            }
            onChange={(event, value) => {
              if (value) setChosenBuildinId(value.id)
            }}
            fullWidth
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    noWrap
                  >
                    {formatBuildingOptionLabel(option)}
                  </Typography>
                  {
                    formatBuildingOptionMeta(option) ? (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        display: 'block',
                        mt: 0.25
                      }}>
                      {formatBuildingOptionMeta(option)}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('choose_building')}
                helperText={t('choose_building_text')}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          color="error" 
          onClick={() => dispatch(closeActiveBuildingSelect())}
        >
          {t('cancel')}
        </Button>
        <Button onClick={selectBuilding} disabled={!selectedBuildingId}>{t('apply')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default BuildingSelectModal;